import { ReactNode } from 'react';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { NetworkPerformanceMetricDrillKey } from 'stores/networkPerformanceStore';

import {
  findRollupByGroupingValue,
  groupingValueFor,
  sumDetailRowsByDimensions,
  totalDimsFor,
} from '../../shared/slideout/rollupHelpers';

import { type BreakdownColumnConfig, type MetricColumn } from './breakdownColumnConfigs';
import { getQueryParams, METRIC_CONFIGS } from './constants';

// --- Row types ---

export type NetworkPerformanceBreakdownRow = {
  entityName: string;
  entityId: string;
  isSubRow?: boolean;
  subRows?: NetworkPerformanceBreakdownRow[];
  values: Record<string, number | null>;
};

const CELL_PADDING = { meta: { padding: '16px 12px' } };
const CELL_PADDING_LEFT = { meta: { padding: '16px 12px 16px 24px' } };

export function getDefaultSort(columnConfig: BreakdownColumnConfig): { id: string; desc: boolean } {
  return { id: columnConfig.defaultSortId, desc: columnConfig.defaultSortDesc };
}

export function buildNetworkPerformanceBreakdownColumns(
  metric: NetworkPerformanceMetricDrillKey,
  entityHeader: string,
  entityCell: (info: CellContext<NetworkPerformanceBreakdownRow, string>) => ReactNode,
  columnConfig: BreakdownColumnConfig
): ColumnDef<NetworkPerformanceBreakdownRow, any>[] {
  const helper = createColumnHelper<NetworkPerformanceBreakdownRow>();
  const { columns } = columnConfig;

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: entityCell,
      size: 320,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    ...columns.map((col, i) =>
      helper.accessor((row) => row.values[col.key] ?? null, {
        id: col.key,
        header: col.header,
        cell: col.renderCell
          ? (info) => col.renderCell!(info.row.original.values)
          : (info) => col.format(info.getValue()),
        size: col.width ?? (i === 0 ? 180 : 200),
        enableSorting: true,
        ...(i === 0 ? CELL_PADDING_LEFT : CELL_PADDING),
      })
    ),
  ];
}

export const getSubRows = (row: NetworkPerformanceBreakdownRow): NetworkPerformanceBreakdownRow[] | undefined =>
  row.subRows;

function readGroupingRows(
  grouping: AnalyticsResponseRollupCollection['groupings'][number],
  nameDimIndex: number,
  idDimIndex: number | undefined,
  metricColumns: MetricColumn[]
): NetworkPerformanceBreakdownRow[] {
  const names = grouping.dimensions.getCurrentDimensionValuesByIndex(nameDimIndex);
  const ids = idDimIndex != null ? grouping.dimensions.getCurrentDimensionValuesByIndex(idDimIndex) : [];
  const metricSeries = metricColumns.map((col) => ({
    key: col.key,
    values: grouping.metrics.getCurrentSeries(col.metric),
  }));

  return names
    .map((name, i) => ({
      entityName: name,
      entityId: ids[i] ?? '',
      values: Object.fromEntries(metricSeries.map((s) => [s.key, s.values[i] ?? null])),
    }))
    .filter((row) => row.entityName);
}

// Read grouping-0 rows (all dims preserved) from the raw response data, since
// AnalyticsResponseRollupCollection filters them out of its groupings array.
function readDetailRows(
  rollups: AnalyticsResponseRollupCollection,
  nameDimIndex: number,
  parentIdDimIndex: number,
  metricColumns: MetricColumn[]
): (NetworkPerformanceBreakdownRow & { parentId: string })[] {
  const { analyticsResponse } = rollups;

  return analyticsResponse.responseData
    .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
    .map((row) => {
      const dimVal = (i: number) => String(row.dimensionValues[i]?.value ?? '');
      const metricVal = (name: string) => {
        // findMetricIndex applies snakeCase to match header names (e.g., 'days_to_start_care:average' → 'days_to_start_care_average')
        const i = analyticsResponse.findMetricIndex(name);
        if (i < 0) return null;
        const raw = row.metricValues[i]?.value;
        return raw != null ? Number(raw) : null;
      };
      return {
        entityName: dimVal(nameDimIndex),
        entityId: '',
        isSubRow: true as const,
        parentId: dimVal(parentIdDimIndex),
        values: Object.fromEntries(metricColumns.map((col) => [col.key, metricVal(col.metric)])),
      };
    })
    .filter((row) => row.entityName);
}

export function buildNetworkPerformanceBreakdownRows(
  metric: NetworkPerformanceMetricDrillKey,
  rollups: AnalyticsResponseRollupCollection,
  columnConfig: BreakdownColumnConfig
): NetworkPerformanceBreakdownRow[] {
  const metricConfig = METRIC_CONFIGS[metric];
  const { columns } = columnConfig;
  const { hasSubRows } = getQueryParams(metric);
  const totalDims = totalDimsFor(rollups);

  // Pivot columns derive their values from cross-dimension aggregation, not from the rollup directly.
  // Exclude them when reading from rollup groupings to avoid reading the wrong metric values.
  const rollupColumns = columns.filter((c) => !c.pivotDimension);
  const pivotColumns = columns.filter((c) => c.pivotDimension);

  // Entity-level: preserve dims 0,1 (entity name+id).
  const entityTarget = groupingValueFor(totalDims, new Set([0, 1]));
  let parentRows: NetworkPerformanceBreakdownRow[];

  if (entityTarget === 0) {
    // 2-dim case (e.g., Engagements with no sub-dimensions): entity rows are grouping 0,
    // which AnalyticsResponseRollupCollection filters out. Read from responseData directly.
    const { analyticsResponse } = rollups;
    parentRows = analyticsResponse.responseData
      .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
      .map((row) => {
        const dimVal = (i: number) => String(row.dimensionValues[i]?.value ?? '');
        const metricVal = (name: string) => {
          const i = analyticsResponse.findMetricIndex(name);
          if (i < 0) return null;
          const raw = row.metricValues[i]?.value;
          return raw != null ? Number(raw) : null;
        };
        return {
          entityName: dimVal(0),
          entityId: dimVal(1),
          values: Object.fromEntries(rollupColumns.map((col) => [col.key, metricVal(col.metric)])),
        };
      })
      .filter((row) => row.entityName);
  } else {
    const entityRollup = findRollupByGroupingValue(rollups, entityTarget);
    if (!entityRollup) return [];
    parentRows = readGroupingRows(entityRollup, 0, 1, rollupColumns);
  }

  // Enrich entity rows with pivoted dimension data (e.g., service refusals by refused-by type).
  if (pivotColumns.length > 0) {
    const pivotDim = pivotColumns[0].pivotDimension!;
    const byEntityByPivot = sumDetailRowsByDimensions(
      rollups,
      'provider_client_id',
      pivotDim,
      metricConfig.primaryMetric
    );
    for (const row of parentRows) {
      const pivotMap = byEntityByPivot.get(row.entityId);
      if (pivotMap) {
        for (const col of pivotColumns) {
          row.values[col.key] = pivotMap.get(col.pivotValue!) ?? null;
        }
      }
    }
  }

  if (!hasSubRows) return parentRows;

  // Sub-entity rows come from the rollup that preserves entity+group but rolls up any trailing dims.
  // For 3-dim queries (entity+group), this is grouping 0 (detail rows, read from responseData).
  // For 4-dim queries (entity+group+type), this is grouping 1 (type rolled up, in the collection).
  const subGroupTarget = groupingValueFor(totalDims, new Set([0, 1, 2]));
  let subRows: (NetworkPerformanceBreakdownRow & { parentId: string })[];

  if (subGroupTarget === 0) {
    // 3-dim case: read from responseData since grouping 0 is filtered from the collection
    subRows = readDetailRows(rollups, 2, 1, rollupColumns);
  } else {
    // 4+ dim case: sub-group rollup is in the collection
    const subGroupRollup = findRollupByGroupingValue(rollups, subGroupTarget);
    if (!subGroupRollup) return parentRows;
    const parentIds = subGroupRollup.dimensions.getCurrentDimensionValuesByIndex(1);
    subRows = readGroupingRows(subGroupRollup, 2, undefined, rollupColumns).map((row, i) => ({
      ...row,
      isSubRow: true as const,
      parentId: parentIds[i] ?? '',
    }));
  }

  // Enrich sub-rows with pivoted dimension data, grouped by group name.
  if (pivotColumns.length > 0) {
    const pivotDim = pivotColumns[0].pivotDimension!;
    const byGroupByPivot = sumDetailRowsByDimensions(rollups, 'group', pivotDim, metricConfig.primaryMetric);
    for (const sub of subRows) {
      const pivotMap = byGroupByPivot.get(sub.entityName);
      if (pivotMap) {
        for (const col of pivotColumns) {
          sub.values[col.key] = pivotMap.get(col.pivotValue!) ?? null;
        }
      }
    }
  }

  const byParent = new Map<string, NetworkPerformanceBreakdownRow[]>();
  for (const sub of subRows) {
    const pid = (sub as { parentId: string }).parentId;
    if (!pid) continue;
    const list = byParent.get(pid);
    if (list) list.push(sub);
    else byParent.set(pid, [sub]);
  }

  return parentRows.map((row) => {
    const children = row.entityId ? byParent.get(row.entityId) : undefined;
    return children?.length ? { ...row, subRows: children } : row;
  });
}
